/**
 * Deleting IndexedDB databases and finding out what actually happened to each one.
 *
 * `indexedDB.deleteDatabase(name)` has deleted nothing by the time it returns. It hands back a
 * request, and the answer arrives later as an event — or never arrives at all. That last case is
 * the one everything here is shaped around: a delete issued against a database some other
 * connection still holds open sits silently in the browser's connection queue and fires nothing,
 * so a wait for every answer is a wait that can never end. Hence the bound.
 *
 * Two callers need exactly this, and each of them used to carry its own copy of it
 * (`df-c5943ac55882`):
 *
 *  - the sign-out wipe in `reload-teardown.ts`, which deletes everything on the device so the
 *    next person to sign in gets none of the previous user's data;
 *  - the one-time cleanup in `legacy-idb-cleanup.ts`, which removes the databases iOS left
 *    behind when the app moved its local storage to SQLite.
 *
 * The two copies had already drifted, and nothing said whether that was deliberate. It is: the
 * sign-out wipe treats a blocked deletion as settled and says nothing about it, because the page
 * is about to reload and what protects the user is the rest of the teardown; the legacy cleanup
 * treats a blocked deletion as a database that is still there, because it can and should try
 * again on a later boot. That difference now lives in each caller as a line that names the
 * outcome it is deciding about, instead of being hidden in two similar-looking handler bodies.
 *
 * So this module deliberately decides NOTHING about severity. It reports what each deletion came
 * to and hands that back. Which outcomes count as a problem, which are worth a Sentry report,
 * and what to do next are the caller's business.
 *
 * Nothing here reaches for a browser global, and every type is structural rather than a DOM type,
 * so a test can hand it plain objects. `reload-teardown.test.ts` and `legacy-idb-cleanup.test.ts`
 * both drive it that way through their own callers.
 */

import { timeout } from '@cf-shared/shared-util';

/** One entry of `indexedDB.databases()`. The name really is optional in `IDBDatabaseInfo`. */
export interface IdbDatabaseInfoLike {
  name?: string;
}

/**
 * What a `deleteDatabase()` request answers with. Structural rather than `IDBOpenDBRequest`, so
 * this file needs no DOM type definitions and a test can hand back a plain object.
 *
 * The handlers take `any`, and that is not laziness. Nothing here reads the event — the reason a
 * deletion failed is read off the REQUEST (see `error`), because the event does not carry one. A
 * structurally-typed event parameter would earn nothing and would stop the browser's real
 * `IDBFactory` being assignable to `IdbForDeletion` at all, since its handlers are declared to
 * take a full DOM `Event`. Callers would then each need a cast.
 */
export interface IdbDeleteRequestLike {
  onsuccess: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onblocked: ((event: any) => void) | null;
  /**
   * Fired for an OPEN request, never for a delete one. Wired anyway, because the sign-out wipe
   * has always wired it and a handler that costs nothing is not worth removing on the strength
   * of a reading of the specification.
   */
  onupgradeneeded?: ((event: any) => void) | null;
  /**
   * Why the deletion failed. Read from the REQUEST rather than from the error event, because
   * the event carries no name, code or message — reporting it would give every field as
   * undefined and we would never learn why (QuotaExceeded, Unknown, ...).
   */
  readonly error?: unknown;
}

/** The two `indexedDB` calls a deleting caller makes, named so a test can supply a fake. */
export interface IdbForDeletion {
  databases(): Promise<IdbDatabaseInfoLike[]>;
  deleteDatabase(name: string): IdbDeleteRequestLike;
}

/** What became of one deletion. Only `Deleted` means the database is really gone. */
export enum IdbDeletionOutcome {
  /** the browser confirmed it: the database is gone */
  Deleted = 'deleted',
  /** the browser refused: the database is still there, and the result carries the reason */
  Failed = 'failed',
  /** another connection still has it open; the deletion may or may not complete later */
  Blocked = 'blocked',
  /** the request asked for an upgrade, which a delete request is not supposed to do */
  UpgradeNeeded = 'upgrade-needed',
  /** no event arrived at all before the wait ran out */
  NoAnswer = 'no-answer',
}

/** What one database's deletion came to. */
export interface IdbDeletionResult {
  name: string;
  outcome: IdbDeletionOutcome;
  /** the reason the browser gave, when the outcome is `Failed`. Read off the request. */
  error?: unknown;
}

export interface IdbDeletionOptions {
  /**
   * How long to wait for the answers before giving up on the ones that have not arrived.
   * Whatever is still unanswered when this fires is reported as `NoAnswer`.
   */
  timeoutMs: number;
  /**
   * Which databases the wait may block on. Absent means "all of them".
   *
   * A name this refuses still gets its delete ISSUED and still gets a result — it is only left
   * out of the thing being waited for. The sign-out wipe needs that for the service worker's own
   * databases, which can never answer while the worker holds them, so waiting on one costs the
   * full bound on every single sign-out and reports a stall that is not one.
   */
  shouldWaitFor?: (name: string) => boolean;
  /** Put in front of every console line, so a reader can tell which caller wrote it. */
  logPrefix?: string;
}

/**
 * Delete every named database and find out what actually happened to each one.
 *
 * Never rejects for a deletion that answers badly — a refusal is a result, not an error. It DOES
 * let a synchronous throw out of `deleteDatabase` propagate, so the caller can report that with
 * the real reason rather than mislabelling it as a database that stayed behind.
 */
export async function deleteIdbDatabasesAsync(
  indexedDb: IdbForDeletion,
  names: string[],
  options: IdbDeletionOptions,
): Promise<IdbDeletionResult[]> {
  const logPrefix = options.logPrefix ?? '';

  // start every entry at NoAnswer, so a deletion the wait never hears from is already correctly
  // described by the time the bound fires
  const results: IdbDeletionResult[] = names.map(
    name => ({ name, outcome: IdbDeletionOutcome.NoAnswer }));

  const answers: Promise<void>[] = [];
  names.forEach((name, index) => {
    // issue the deletion OUTSIDE the promise, so a synchronous throw travels up to the caller's
    // catch instead of being swallowed into a rejected answer nobody reads
    const request = indexedDb.deleteDatabase(name);
    const answer = new Promise<void>(resolve => {
      request.onsuccess = () => {
        results[index].outcome = IdbDeletionOutcome.Deleted;
        console.log(`${logPrefix}Deleted database ${name}`);
        resolve();
      };
      request.onerror = () => {
        results[index].outcome = IdbDeletionOutcome.Failed;
        results[index].error = request.error;
        console.error(`${logPrefix}Failed to delete database ${name}`, request.error);
        resolve();
      };
      request.onblocked = () => {
        results[index].outcome = IdbDeletionOutcome.Blocked;
        console.warn(`${logPrefix}Blocked deletion of database ${name}`);
        resolve();
      };
      request.onupgradeneeded = () => {
        results[index].outcome = IdbDeletionOutcome.UpgradeNeeded;
        console.warn(`${logPrefix}Upgrade needed for deletion of database ${name}`);
        resolve();
      };
    });

    // only wait on the databases this caller says can actually finish
    if (options.shouldWaitFor?.(name) ?? true) {
      answers.push(answer);
    }
  });

  // BOUNDED: a deletion nobody can finish fires no event at all, so waiting for every answer can
  // wait for ever. Anything still unanswered keeps the NoAnswer it started with. The answers
  // never reject, so the only thing this catch can be swallowing is the bound itself.
  await timeout(options.timeoutMs, Promise.all(answers)).catch(() => undefined);
  return results;
}
